import L from "leaflet";
import { useEffect, useMemo } from "react";
import { MapContainer, Polygon, Popup, TileLayer, useMap } from "react-leaflet";
import { EmptyState } from "@/shared/components/EmptyState";
import { geoJsonToLeafletPositions, geometryToLatLngs } from "@/shared/map/geojson";
import type { Territory } from "../services/territory.service";

const POLYGON_COLOR = "#1677ff";

function FitToPolygon({ positions }: { positions: [number, number][] }) {
	const map = useMap();

	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		map.fitBounds(L.latLngBounds(positions), { padding: [20, 20], maxZoom: 16 });
	}, [map, positions]);

	return null;
}

interface TerritoryPolygonMapProps {
	territory: Territory;
}

/** Read-only map showing a single territory polygon */
export function TerritoryPolygonMap({ territory }: TerritoryPolygonMapProps) {
	const boundsPositions = useMemo(
		() => (territory.coordinates ? geometryToLatLngs(territory.coordinates) : []),
		[territory]
	);
	const positions = useMemo(
		() =>
			territory.coordinates?.coordinates?.length
				? geoJsonToLeafletPositions(territory.coordinates.coordinates)
				: [],
		[territory]
	);

	if (boundsPositions.length === 0) {
		return <EmptyState description="Hudud koordinatalari topilmadi" />;
	}

	return (
		<div className="h-[360px] w-full rounded-lg overflow-hidden border border-gray-200">
			<MapContainer
				center={boundsPositions[0]}
				zoom={13}
				className="h-full w-full"
				scrollWheelZoom={false}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<FitToPolygon positions={boundsPositions} />
				<Polygon
					positions={positions as any}
					pathOptions={{
						color: POLYGON_COLOR,
						fillColor: POLYGON_COLOR,
						fillOpacity: 0.3,
						weight: 2,
					}}
				>
					<Popup>
						<div className="text-sm min-w-[160px]">
							<p className="font-semibold mb-1">{territory.name}</p>
							<p className="mb-1">
								Yuzasi: <strong>{territory.area?.toLocaleString("uz-UZ") ?? "—"} m²</strong>
							</p>
							<p className="mb-0">
								Perimetr: <strong>{territory.perimeter?.toLocaleString("uz-UZ") ?? "—"} m</strong>
							</p>
						</div>
					</Popup>
				</Polygon>
			</MapContainer>
		</div>
	);
}
